import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Users as UsersIcon, ShieldCheck, Activity, TrendingUp, Sparkles, Trash2, Search,
  BookOpen, DollarSign, ScrollText, CheckCircle2, XCircle, Plus, Save,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, sub }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid={`admin-stat-${label}`}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon size={16} /><span className="text-xs uppercase tracking-[0.14em]">{label}</span></div>
    <p className="mt-2 font-display text-3xl text-foreground">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </Card>
);

const Overview = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="py-16 text-center text-muted-foreground">Loading overview…</div>;
  const max = Math.max(1, ...data.signups.map((s) => s.count));
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={UsersIcon} label="Total Users" value={data.users.total} sub={`${data.users.onboarded} onboarded`} />
        <Stat icon={CheckCircle2} label="Verified" value={data.users.verified} sub={`${data.users.unverified} unverified`} />
        <Stat icon={Activity} label="Active 7d" value={data.users.active_7d} sub={`${data.users.active_30d} in 30d`} />
        <Stat icon={ShieldCheck} label="Admins" value={data.users.admins} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Stat icon={TrendingUp} label="Daily Check-ins" value={data.engagement.daily_checkins} />
        <Stat icon={Sparkles} label="AI Requests" value={data.engagement.ai_requests} />
        <Stat icon={Activity} label="Total Logins" value={data.engagement.logins} />
      </div>
      <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
        <h3 className="font-display text-lg">Signups — last 14 days</h3>
        <div className="mt-5 flex items-end gap-1.5 h-32" data-testid="admin-signups-chart">
          {data.signups.map((s) => (
            <div key={s.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${s.date}: ${s.count}`}>
              <div className="w-full rounded-t bg-[hsl(var(--gold))]/70" style={{ height: `${(s.count / max) * 100}%`, minHeight: s.count ? 4 : 0 }} />
              <span className="text-[9px] text-muted-foreground">{s.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
          <h3 className="font-display text-lg">Plans</h3>
          <div className="mt-3 space-y-2">{data.plans.map((p) => (<div key={p.plan} className="flex justify-between text-sm"><span className="capitalize">{p.plan}</span><Badge variant="secondary">{p.count}</Badge></div>))}</div>
        </Card>
        <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
          <h3 className="font-display text-lg">Personas</h3>
          <div className="mt-3 space-y-2">{data.personas.map((p) => (<div key={p.persona} className="flex justify-between text-sm"><span className="truncate pr-2">{p.persona}</span><Badge variant="secondary">{p.count}</Badge></div>))}</div>
        </Card>
      </div>
    </div>
  );
};

const UsersTab = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => { api.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data.users)).catch(() => {}); }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = async (id, changes) => {
    try { await api.patch(`/admin/users/${id}`, changes); toast.success("User updated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <Card className="p-0 border-border shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9 rounded-xl" data-testid="admin-user-search" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>User</TableHead><TableHead>Plan</TableHead><TableHead>Verified</TableHead>
            <TableHead>Status</TableHead><TableHead>Admin</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.email}`}>
                <TableCell><div className="font-medium">{u.name} {u.is_master && <Badge className="ml-1">Master</Badge>}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                <TableCell>
                  <Select value={u.plan || "daily"} onValueChange={(v) => patch(u.id, { plan: v })}>
                    <SelectTrigger className="h-8 w-32 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{["daily", "intentional", "executive"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{u.email_verified ? <CheckCircle2 size={16} className="text-[hsl(152_35%_40%)]" /> : <XCircle size={16} className="text-muted-foreground" />}</TableCell>
                <TableCell>
                  <Switch checked={u.status !== "disabled"} onCheckedChange={(on) => patch(u.id, { status: on ? "active" : "disabled" })} disabled={u.is_master} data-testid={`admin-status-${u.email}`} />
                </TableCell>
                <TableCell>
                  <Switch checked={!!u.is_admin} onCheckedChange={(on) => patch(u.id, { is_admin: on })} disabled={u.is_master} data-testid={`admin-toggle-${u.email}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => del(u)} disabled={u.is_master || u.id === user?.id} data-testid={`admin-delete-${u.email}`}><Trash2 size={15} className="text-muted-foreground" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

const emptyPost = { title: "", category: "", excerpt: "", body: "", read: "4 min", published: true };

const ContentTab = () => {
  const [tiers, setTiers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(null);

  const loadPosts = () => api.get("/admin/content/journal").then((r) => setPosts(r.data.posts)).catch(() => {});
  useEffect(() => {
    api.get("/admin/content/pricing").then((r) => setTiers(r.data.tiers)).catch(() => {});
    loadPosts();
  }, []);

  const savePricing = async () => {
    try { await api.put("/admin/content/pricing", { tiers }); toast.success("Pricing saved."); }
    catch { toast.error("Could not save pricing."); }
  };
  const updateTier = (i, key, val) => setTiers((t) => t.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));

  const savePost = async () => {
    try {
      if (draft.id) await api.put(`/admin/content/journal/${draft.id}`, draft);
      else await api.post("/admin/content/journal", draft);
      toast.success("Article saved."); setDraft(null); loadPosts();
    } catch { toast.error("Could not save article."); }
  };
  const delPost = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/admin/content/journal/${p.id}`); toast.success("Deleted."); loadPosts(); };

  return (
    <div className="space-y-6">
      <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
        <div className="flex items-center justify-between"><h3 className="font-display text-lg flex items-center gap-2"><DollarSign size={18} /> Pricing Tiers</h3><Button onClick={savePricing} className="rounded-xl" data-testid="admin-save-pricing"><Save size={15} className="mr-2" />Save Pricing</Button></div>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {tiers.map((t, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-2">
              <Input value={t.name || ""} onChange={(e) => updateTier(i, "name", e.target.value)} className="rounded-lg font-medium" />
              <div className="flex gap-2"><Input value={t.price || ""} onChange={(e) => updateTier(i, "price", e.target.value)} className="rounded-lg" placeholder="Price" /><Input value={t.cadence || ""} onChange={(e) => updateTier(i, "cadence", e.target.value)} className="rounded-lg" placeholder="/ mo" /></div>
              <Input value={t.tagline || ""} onChange={(e) => updateTier(i, "tagline", e.target.value)} className="rounded-lg text-sm" placeholder="Tagline" />
              <Input value={t.cta || ""} onChange={(e) => updateTier(i, "cta", e.target.value)} className="rounded-lg text-sm" placeholder="CTA label" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border shadow-[var(--shadow-sm)]">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg flex items-center gap-2"><BookOpen size={18} /> Journal ({posts.length})</h3>
          <Button onClick={() => setDraft({ ...emptyPost })} className="rounded-xl" data-testid="admin-new-article"><Plus size={15} className="mr-2" />New Article</Button>
        </div>
        {draft && (
          <div className="mt-4 rounded-xl border border-[hsl(var(--gold))]/40 bg-secondary/40 p-4 space-y-3" data-testid="admin-article-editor">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" className="rounded-lg" data-testid="admin-article-title" />
              <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="Category" className="rounded-lg" />
            </div>
            <Input value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} placeholder="Excerpt" className="rounded-lg" />
            <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Article body" className="rounded-lg min-h-28" />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm"><Switch checked={draft.published} onCheckedChange={(v) => setDraft({ ...draft, published: v })} /> Published</label>
              <div className="flex-1" />
              <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
              <Button onClick={savePost} className="rounded-xl" data-testid="admin-save-article"><Save size={15} className="mr-2" />Save</Button>
            </div>
          </div>
        )}
        <div className="mt-4 divide-y divide-border">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-3">
              <div className="flex-1 min-w-0"><p className="font-medium truncate">{p.title}</p><p className="text-xs text-muted-foreground">{p.category} · {p.published ? "Published" : "Draft"}</p></div>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setDraft(p)}>Edit</Button>
              <Button variant="ghost" size="icon" onClick={() => delPost(p)}><Trash2 size={15} className="text-muted-foreground" /></Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const AuditTab = () => {
  const [events, setEvents] = useState([]);
  const [type, setType] = useState("");
  useEffect(() => { api.get(`/admin/audit${type ? `?event_type=${type}` : ""}`).then((r) => setEvents(r.data.events)).catch(() => {}); }, [type]);
  const TYPES = ["", "register", "login", "email_verified", "reset_requested", "password_reset", "ai_advise", "admin_update_user", "admin_delete_user"];
  return (
    <Card className="p-0 border-border shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="p-4 border-b border-border flex items-center gap-3">
        <ScrollText size={16} className="text-muted-foreground" />
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-56 rounded-lg" data-testid="audit-filter"><SelectValue placeholder="All events" /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t || "all"} value={t || "all"}>{t || "All events"}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Email</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
          <TableBody>
            {events.map((e) => (
              <TableRow key={e.id}><TableCell><Badge variant="secondary">{e.type}</Badge></TableCell><TableCell className="text-sm">{e.email || e.user_id || "—"}</TableCell><TableCell className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  return (
    <div className="space-y-6 pb-16" data-testid="admin-page">
      <SectionHeader title="Admin Control Center" subtitle="Manage members, monitor growth and engagement, curate content, and review the audit trail." />
      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-5"><Overview /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="content" className="mt-5"><ContentTab /></TabsContent>
        <TabsContent value="audit" className="mt-5"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
